import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";

const STEPS = [
  { num: "01", title: "Discover", desc: "We learn about your business, audience and goals." },
  { num: "02", title: "Design", desc: "We create the visual direction and user experience." },
  { num: "03", title: "Build", desc: "We turn the design into a fast, responsive website." },
  { num: "04", title: "Refine", desc: "We test, optimise and polish every detail." },
  { num: "05", title: "Launch", desc: "Your website goes live and your business is ready to grow." },
];

const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.6"] });
  const lineScale = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section id="process" data-testid="process-section" className="relative z-10 py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <SectionHeading
          kicker="Process"
          title="From Idea To Launch."
          sub="A clear, cinematic journey — five stages from first conversation to a website your customers remember."
          testId="process-heading"
        />
        <div ref={ref} className="relative mt-20 mx-auto max-w-3xl">
          <div className="absolute left-[19px] sm:left-1/2 top-0 bottom-0 w-px bg-white/10 sm:-translate-x-1/2" />
          <motion.div
            className="glow-line absolute left-[19px] sm:left-1/2 top-0 bottom-0 w-px origin-top bg-white sm:-translate-x-1/2"
            style={{ scaleY: lineScale }}
          />
          <div className="space-y-14">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} i={i * 0.5}>
                <div
                  data-testid={`process-step-${i}`}
                  className={`relative flex items-start gap-7 sm:gap-0 ${i % 2 ? "sm:flex-row-reverse" : ""}`}
                >
                  <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/25 bg-[#0a0a0a] font-display text-[11px] font-semibold tracking-widest text-white sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                    {s.num}
                  </div>
                  <div className={`sm:w-1/2 ${i % 2 ? "sm:pr-16 sm:text-right" : "sm:pl-16 sm:ml-auto"}`}>
                    <div className="rounded-2xl glass p-6 transition-[background-color,border-color] duration-500 hover:bg-white/[0.06]">
                      <h3 className="font-display text-xl font-semibold tracking-tight text-white">{s.title}</h3>
                      <p className="mt-2 text-sm font-light leading-relaxed text-white/50">{s.desc}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
